import {
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
} from '@mui/material';
import { dialogOverlay, StyledDialogActions, SuccessActionButton } from '../styles';
import { GenericModal } from '@/components/shared/GenericModal';
import { primary } from '@/theme/colors';
import { formatEnumForDisplay, hexColorWithAlpha } from '@/utils';
import { useIdmsData } from '@/hooks/useIdmsData';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { pipeSupportEnum } from '@/constants';
import { IdmsUploadsTypeEnum } from '@/__generated__/graphql';
import { ValidateFileModal } from './ValidateFileModal';
import { FileExceptionModal } from './FileExceptionModal';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';

type Props = {
  open: boolean;
  handleClose: () => void;
  file: File | undefined;
  fileInputReference: React.MutableRefObject<HTMLInputElement | null | undefined>;
  handleCloseModals: () => void
};

export const PipeSupportModal = ({ open, handleClose, file, fileInputReference, handleCloseModals }: Props) => {
  const structureId = useGetStructureId()
  const {
    setSelectedFileType,
    selectedFileType,
    isValidating,
    fileExceptionsModal,
    uploadedCsvParsedData,
    idmsValidationErrors,
    openConfirmationDialog,
    idmsUploadLoading,
    handleIdmsFileUpload,
    handleXlsxFileUpload,
    handleCsvFileUpload,
    setFileExceptionsModal,
    setOpenConfirmationDialog,
  } = useIdmsData(structureId)

  const pipeSupportOptions = Object.entries(pipeSupportEnum)

  const handleFileUpload = () => {
    if (!file || !selectedFileType) return;

    // Check if file is XLSX or CSV based on extension
    const isXlsx = file.name.toLowerCase().endsWith('.xlsx');

    if (isXlsx) {
      handleXlsxFileUpload(file, selectedFileType);
    } else {
      handleCsvFileUpload(file, selectedFileType);
    }
  };
  const handleCloseFileExceptionModal = () => {
    setFileExceptionsModal(false);
    setSelectedFileType(undefined);
  };
  const handleCancelUpload = () => {
    setSelectedFileType(undefined);
    setOpenConfirmationDialog(false);
  };

  return (
    <>
      <GenericModal
        open={open}
        handleClose={handleClose}
        crossIconStyle={{ backgroundColor: 'transparent', color: 'primary' }}
        sx={{
          ...dialogOverlay,
          left: '37% !important',
          width: 536,
          minHeight: 572,
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent',
            backdropFilter: 'none',
          },
        }}
      >
        <List sx={{ maxHeight: 392, overflowY: 'auto' }}>
          {pipeSupportOptions.map(([value, key]) => (
            <ListItem
              data-testid="file-type-list-item"
              disableGutters
              key={key}
              sx={{
                py: 0,
                borderTop: 1,
                borderColor: 'divider',
                '&:hover': { backgroundColor: hexColorWithAlpha(primary.light, 20) },
              }}
            >
              <ListItemButton
                sx={{ '&:hover': { background: 'none' } }}
                onClick={() => setSelectedFileType(key as IdmsUploadsTypeEnum)}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Radio checked={selectedFileType === key} />
                </ListItemIcon>
                <ListItemAvatar sx={{ minWidth: '30px', pl: 1, pr: 2 }}>
                  <img src="/assets/icons/file-icon.svg" alt="File_Preview" width={30} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.4rem', fontWeight: '600' }}
                  primary={formatEnumForDisplay(value)}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <StyledDialogActions>
          <SuccessActionButton
            variant="contained"
            onClick={handleFileUpload}
            disabled={!selectedFileType}
          >
            Confirm
          </SuccessActionButton>
          <Button onClick={handleClose} variant="text" sx={{ mr: 1, height: '36px' }}>
            Cancel
          </Button>
        </StyledDialogActions>
      </GenericModal>
      <ValidateFileModal open={isValidating} />

      <ConfirmationDialog
        title="Success"
        description="The file has been uploaded and validated successfully. Kindly confirm to proceed with importing the data into our system"
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        isOpen={openConfirmationDialog}
        onConfirm={() => file && handleIdmsFileUpload(file)}
        onCancel={handleCancelUpload}
        handleClose={() => { }}
        handleCloseModals={handleCloseModals}
        loading={{
          state: idmsUploadLoading,
          text: 'Uploading...',
        }}
      />
      <FileExceptionModal
        open={fileExceptionsModal}
        idmsUploadLoading={idmsUploadLoading}
        selectedFileType={selectedFileType}
        handleIdmsFileUpload={handleIdmsFileUpload}
        handleClose={handleCloseFileExceptionModal}
        uploadedCsvParsedData={uploadedCsvParsedData}
        idmsValidationErrors={idmsValidationErrors}
        fileInputReference={fileInputReference}
        file={file}
      />
    </>

  );
};
